import { forwardRef, useEffect, useMemo, useRef } from "react";
import * as React from "react";
import cn from "classnames";
import Control, { ControlProps, ControlRef } from "@ebsco-ui/control";
import { faCheck, faMinus } from "@fortawesome/pro-solid-svg-icons";
import { combineRefs, useControlledValue } from "../common/utils";

import "./checkbox.scss";

export interface CheckboxProps extends Omit<ControlProps, "icon" | "type"> {
  isIndeterminate?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  checked?: boolean;
}

export const Checkbox = forwardRef<ControlRef, CheckboxProps>(
  (
    {
      className,
      isIndeterminate = false,
      onChange: handleChange,
      checked: externalChecked,
      ...rest
    }: CheckboxProps,
    forwardedRef,
  ) => {
    const [checked, setChecked] = useControlledValue(externalChecked, false);
    const checkboxRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (checkboxRef && checkboxRef.current) {
        checkboxRef.current.indeterminate = isIndeterminate;
      }
    }, [isIndeterminate]);

    const mergedRefs = combineRefs(forwardedRef, checkboxRef);

    const icon = useMemo(() => {
      if (isIndeterminate && !checked) {
        return faMinus;
      }

      return faCheck;
    }, [checked, isIndeterminate]);

    return (
      <Control
        checked={checked}
        className={cn("eb-checkbox", className)}
        icon={icon}
        onChange={(e) => {
          handleChange && handleChange(e);
          setChecked((prevChecked) => !prevChecked);
        }}
        ref={mergedRefs}
        type="checkbox"
        {...rest}
      />
    );
  },
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
